import { inject, Injectable } from "@angular/core";
import { GoogleAuthService } from "../../../api/services";
import { catchError, Observable, throwError } from "rxjs";
import { LoggedUserService } from "@keus-template/auth";
import { environment } from "../../../environments/environment";
import { GoogleAuthControllerTriggerUserAuth$Params } from "../../../api/fn/google-auth/google-auth-controller-trigger-user-auth";

@Injectable({
  providedIn: "root",
})
export class GoogleCalendarAuthApiService {
  /** Injected service for logged user */
  private readonly _loggedUserService: LoggedUserService =
    inject(LoggedUserService);

  /** Service for interacting with the Google Calendar Auth API. */
  private readonly _googleAuthService: GoogleAuthService =
    inject(GoogleAuthService);

  public triggerUserAuth$(): Observable<void> {
    const currentUser = this._loggedUserService.currentUser();
    
    // Validate required data before making API call
    if (!currentUser?.email) {
      return throwError(() => new Error('User email is required for Google Calendar authentication'));
    }

    if (!environment.tenant) {
      return throwError(() => new Error('Tenant configuration is missing'));
    }

    const params: GoogleAuthControllerTriggerUserAuth$Params = {
      tenant: environment.tenant,
      email: currentUser.email,
    };

    return this._googleAuthService
      .googleAuthControllerTriggerUserAuth(params)
      .pipe(
        catchError((error) => {
          console.error("Google Calendar auth API error:", error);

          // Safely handle redirect URL from error response
          try {
            const parsedError = error?.error ? JSON.parse(error.error) : null;
            
            if (parsedError?.redirectUrl && this._isValidUrl(parsedError.redirectUrl)) {
              window.location.href = parsedError.redirectUrl;
              // Return empty observable since we're redirecting
              return new Observable<void>(subscriber => {
                // Don't complete - let the page navigation handle this
              });
            }
          } catch (parseError) {
            console.warn("Failed to parse error response:", parseError);
          }

          // Enhanced error logging for OAuth configuration issues
          if (error?.status === 400 || error?.message?.includes('redirect_uri')) {
            console.error("❌ Google OAuth Configuration Error - Backend needs to configure redirect_uri:", 
              `${window.location.origin}/google-calendar-callback`);
          }

          // Re-throw error for retry logic in components
          return throwError(() => error);
        }),
      );
  }

  /**
   * Validate if URL is safe for redirect
   */
  private _isValidUrl(url: string): boolean {
    try {
      const parsedUrl = new URL(url);
      // Only allow HTTPS URLs from Google domains
      return parsedUrl.protocol === 'https:' && 
             (parsedUrl.hostname.endsWith('.google.com') || 
              parsedUrl.hostname.endsWith('.googleapis.com'));
    } catch {
      return false;
    }
  }

  /**
   * Complete the authentication process by calling the GoogleAuthControllerCallback method.
   * @param code The authorization code received from the Google OAuth flow.
   * @param state The state parameter used to prevent CSRF attacks.
   * @returns An Observable that completes when the authentication process is finished.
   */
  public completeAuthentication$(
    code: string,
    state: string,
  ): Observable<void> {
    return this._googleAuthService.googleAuthControllerCallback({
      code,
      state,
    });
  }
}
